import moment from 'moment';
import { createRoomCleaning, getRoomCleanings } from '../services/api';

export function isWalkInDirtyRoom(room) {
  return isRoomDirty(room);
}

export function isWalkInWebCheckInHeld(room) {
  return room?.walk_in_web_checkin === true || room?.has_active_web_checkin === true;
}

export function isWalkInAwarenessHold(room) {
  return (
    room?.walk_in_pending_checkout === true ||
    room?.walk_in_overdue_checkout === true ||
    room?.is_maintenance_blocked === true ||
    isWalkInWebCheckInHeld(room)
  );
}

export function isRoomDirty(room) {
  return String(room?.clean_status || '').toLowerCase() === 'dirty';
}

/** Patch a single room's clean_status in walk-in all-rooms payload without refetching. */
export function patchWalkInRoomCleanStatus(prevData, roomId, cleanStatus = 'clean') {
  if (!prevData?.room_types || roomId == null) return prevData;
  const targetId = Number(roomId);
  let found = false;
  const room_types = prevData.room_types.map((group) => ({
    ...group,
    rooms: (group.rooms || []).map((room) => {
      if (Number(room.id) !== targetId) return room;
      found = true;
      return { ...room, clean_status: cleanStatus };
    }),
  }));
  if (!found) return prevData;
  return { ...prevData, room_types };
}

function isOpenCleaningStatus(status) {
  const value = String(status || '').toUpperCase();
  return value === 'PENDING' || value === 'IN_PROGRESS';
}

/** Prefer the job already in progress, then the newest open job. */
export function pickOpenCleaningId(cleanings = []) {
  const open = (cleanings || []).filter((cleaning) => isOpenCleaningStatus(cleaning?.status));
  if (!open.length) return null;
  const inProgress = open.find((cleaning) => String(cleaning.status).toUpperCase() === 'IN_PROGRESS');
  return Number((inProgress || open[0]).id);
}

/**
 * Resolve an open housekeeping cleaning id for a dirty room.
 * Reuses PENDING or IN_PROGRESS work for that room. Only creates a checkout
 * task when the room has no open cleaning yet.
 */
export async function resolveWalkInHousekeepingCleaningId(roomId, pendingMap = {}) {
  const key = String(roomId);
  const cached = pendingMap[key];
  if (cached) return Number(cached);

  const list = await getRoomCleanings({
    room_id: roomId,
    cleaning_date: moment().format('YYYY-MM-DD'),
    page_size: 20,
  });
  const results = Array.isArray(list) ? list : (list?.results || []);
  const existingId = pickOpenCleaningId(results);
  if (existingId) return existingId;

  const created = await createRoomCleaning({
    room: roomId,
    cleaning_date: moment().format('YYYY-MM-DD'),
    cleaning_type: 'CHECKOUT',
    status: 'PENDING',
  });
  if (!created?.id) {
    throw new Error('Could not create housekeeping task for this room.');
  }
  return Number(created.id);
}
